import React, { useState, useEffect } from 'react';
import { UniversalPage, UserList, User, StatusActionButton } from '../../../shared/ui';
import { QuestionV2, questionsV2Api, QuestionMember } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import AddParticipantModal from './AddParticipantModal';
import './QuestionInfoPage.css';
import { questionMemberAddPermissionsApi } from '../../../api/questionMemberAddPermissionsApi';
import { sitesV2Api, SiteMember, SiteMemberRole } from '../../../api/sitesV2Api';
import config from '../../../config/config';

interface QuestionInfoPageProps {
  isOpen: boolean;
  question: QuestionV2 | null;
  siteId: number;
  onClose: () => void;
  onQuestionUpdated?: () => void;
}

const QuestionInfoPage: React.FC<QuestionInfoPageProps> = ({
  isOpen,
  question,
  siteId,
  onClose,
  onQuestionUpdated
}) => {
  const { user } = useAuth();
  const [participants, setParticipants] = useState<QuestionMember[]>([]);
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingParticipant, setRemovingParticipant] = useState<number | null>(null);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [closingQuestion, setClosingQuestion] = useState(false);
  const [reopeningQuestion, setReopeningQuestion] = useState(false);
  const [addableRoleIds, setAddableRoleIds] = useState<number[] | null>(null);

  useEffect(() => {
    if (isOpen && question) {
      loadData();
    }
  }, [isOpen, question, siteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!question) {
        setError('Дискуссия не найдена');
        setLoading(false);
        return;
      }

      const [participantsData, membersData, roleIdsData] = await Promise.all([
        questionsV2Api.getQuestionMembers(siteId, question!.id),
        sitesV2Api.getSiteMembers(siteId),
        questionMemberAddPermissionsApi.getAddableRoleIds(siteId, question!.typeId || 1)
      ]);
      
      setParticipants(participantsData);
      setMembers(membersData);
      setAddableRoleIds(roleIdsData);
    } catch (err) {
      console.error('Error loading question info:', err);
      setError(`Ошибка загрузки информации о ${getContentTypeNameGenitive()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (userId: number) => {
    if (!question) return;
    
    try {
      await questionsV2Api.addQuestionMember(siteId, question.id, userId);
      await loadData(); // Перезагружаем данные
    } catch (err: any) {
      console.error('Error adding participant:', err);
      setError('Ошибка добавления участника');
      throw err; // Пробрасываем ошибку для обработки в модалке
    }
  };

  const handleRemoveParticipant = async (userId: string) => {
    if (!question) return;
    
    const userIdNumber = parseInt(userId);
    try {
      setRemovingParticipant(userIdNumber);
      await questionsV2Api.removeQuestionMember(siteId, question.id, userIdNumber);
      await loadData(); // Перезагружаем данные
    } catch (err: any) {
      console.error('Error removing participant:', err);
      setError('Ошибка исключения участника');
    } finally {
      setRemovingParticipant(null);
    }
  };

  const handleCloseQuestion = async () => {
    if (!question) return;
    
    try {
      setClosingQuestion(true);
      await questionsV2Api.closeQuestion(siteId, question.id);
      onQuestionUpdated?.(); // Обновляем данные вопроса в родительском компоненте
      onClose(); // Закрываем страницу после успешного закрытия вопроса
    } catch (err: any) {
      console.error('Error closing question:', err);
      setError(`Ошибка закрытия ${getContentTypeNameGenitive()}`);
    } finally {
      setClosingQuestion(false);
    }
  };

  const handleReopenQuestion = async () => {
    if (!question) return;
    
    try {
      setReopeningQuestion(true);
      await questionsV2Api.reopenQuestion(siteId, question.id);
      onQuestionUpdated?.(); // Обновляем данные вопроса в родительском компоненте
      onClose(); // Закрываем страницу после успешного возобновления вопроса
    } catch (err: any) {
      console.error('Error reopening question:', err);
      setError(`Ошибка возобновления ${getContentTypeNameGenitive()}`);
    } finally {
      setReopeningQuestion(false);
    }
  };

  const isParticipant = (userId: number) => {
    return participants.some(p => p.userId === userId);
  };

  const isCreator = (userId: number) => {
    return userId === question?.user?.id;
  };

  // Функция для получения названия типа контента
  const getContentTypeName = () => {
    switch (question?.typeId) {
      case 1:
        return 'дискуссия';
      case 2:
        return 'заметка';
      case 3:
        return 'заявка';
      default:
        return 'дискуссия';
    }
  };

  // Функция для получения текста статуса
  const getStatusText = (isClosed: boolean) => {
    switch (question?.typeId) {
      case 1:
        return isClosed ? 'Закрыт' : 'Активен';
      case 2:
        return isClosed ? 'Закрыта' : 'Активна';
      case 3:
        return isClosed ? 'Выполнена' : 'В работе';
      default:
        return isClosed ? 'Закрыт' : 'Активен';
    }
  };

  // Функция для получения текста кнопки "Закрыть"
  const getCloseButtonText = () => {
    switch (question?.typeId) {
      case 1:
        return 'Закрыть дискуссию';
      case 2:
        return 'Закрыть заметку';
      case 3:
        return 'Отметить как выполненную';
      default:
        return 'Закрыть дискуссию';
    }
  };

  // Функция для получения текста кнопки "Возобновить"
  const getReopenButtonText = () => {
    switch (question?.typeId) {
      case 1:
        return 'Возобновить дискуссию';
      case 2:
        return 'Возобновить заметку';
      case 3:
        return 'Возобновить заявку';
      default:
        return 'Возобновить дискуссию';
    }
  };

  // Функция для получения названия типа контента в родительном падеже
  const getContentTypeNameGenitive = () => {
    switch (question?.typeId) {
      case 1:
        return 'дискуссии';
      case 2:
        return 'заметки';
      case 3:
        return 'заявки';
      default:
        return 'дискуссии';
    }
  };

  // Функция для получения правильного названия участников с окончаниями
  const getMembersLabel = (count: number) => {
    // Для всех типов контента используем "участник"
    if (count === 1) {
      return 'участник';
    } else if (count >= 2 && count <= 4) {
      return 'участника';
    } else {
      return 'участников';
    }
  };

  const getParticipantMembers = () => {
    return members.filter(member => isParticipant(member.userId) || isCreator(member.userId));
  };

  const getParticipantUsers = (): User[] => {
    return getParticipantMembers().map(member => {
      const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
      const avatarUrl = member.avatarUrl 
        ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
        : undefined;
      
      return {
        userId: member.userId.toString(),
        userName: member.userName || 'Неизвестный',
        // Используем новые роли если есть, иначе fallback на старую роль
        roles: member.roles && member.roles.length > 0 
          ? member.roles.filter((r: SiteMemberRole) => r.isActive).map((r: SiteMemberRole) => ({ 
              id: r.id, 
              roleName: r.roleName, 
              roleId: r.roleId,
              hierarchyLevel: r.hierarchyLevel 
            }))
          : undefined,
        userRole: member.roleName || (isCreator(member.userId) ? 'Создатель' : 'Участник'), // Fallback
        isCreator: isCreator(member.userId),
        avatarUrl: avatarUrl
      };
    });
  };

  const getAvailableMembers = () => {
    return members.filter(member => !isParticipant(member.userId) && !isCreator(member.userId));
  };

  if (!question) {
    return null;
  }

  return (
    <UniversalPage
      isOpen={isOpen}
      title={`${getContentTypeName()} от ${question.user?.name || 'Неизвестный'}`}
      onClose={onClose}
      clickable={true}
      centered={true}
      className="question-info-page"
      infoRow={{
        members: {
          count: participants.length,
          getLabel: getMembersLabel
        },
        status: {
          text: getStatusText(question.closed),
          type: question.closed ? 'closed' : 'active'
        }
      }}
    >
      {/* Кнопка управления статусом вопроса */}
      {!question.closed ? (
        <StatusActionButton 
          variant="warning"
          loading={closingQuestion}
          onClick={handleCloseQuestion}
          disabled={closingQuestion}
          margin="0 20px 20px 20px"
          width="calc(100% - 40px)"
        >
          {getCloseButtonText()}
        </StatusActionButton>
      ) : (
        <StatusActionButton 
          variant="success"
          loading={reopeningQuestion}
          onClick={handleReopenQuestion}
          disabled={reopeningQuestion}
          margin="0 20px 20px 20px"
          width="calc(100% - 40px)"
        >
          {getReopenButtonText()}
        </StatusActionButton>
      )}

      {error && (
        <div className="error-message">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner">
            <i className="ri-loader-4-line ri-spin"></i>
          </div>
          <span>Загрузка информации...</span>
        </div>
      ) : (
        <UserList
          users={getParticipantUsers()}
          onAddUser={() => setShowAddParticipantModal(true)}
          onRemoveUser={handleRemoveParticipant}
          canRemoveUser={(userId) => {
            const userIdNumber = parseInt(userId);
            return !isCreator(userIdNumber); // Создатель не может удалить себя
          }}
          addButtonText="Добавить участника"
          addButtonSubtext="Нажмите для выбора"
          showRemoveButton={false}
        />
      )}

      {/* Модальное окно добавления участника */}
      <AddParticipantModal
        isOpen={showAddParticipantModal}
        siteId={siteId}
        questionId={question?.id || 0}
        availableMembers={getAvailableMembers()}
        onClose={() => setShowAddParticipantModal(false)}
        onAddParticipant={handleAddParticipant}
        addableRoleIds={addableRoleIds}
      />
    </UniversalPage>
  );
};

export default QuestionInfoPage;
